"use strict";

var LoggerInstance = require("js-logger").get("SmartyStreetsVerify");

var AccountApi = require("../api/AccountApi");

var ApiHelper = require("../shared/ApiHelper");

var Constants = require("../designer/Constants");

var smartyPopupSelector = ".smarty-popup";
var smartyPopupTypedAddressSelector = ".smarty-popup-typed-address";
var smartyGoBackSelector = ".smarty-abort";
var smartyConstituentCertifiedSelector = ".smarty-choice-override";
var smartySecondaryInputSelector = ".smarty-popup-secondary-number-input-box";
var smartySecondarySubmitSelector = ".smarty-popup-secondary-number-form-submit-button";
var smartyAmbiguousChoiceSelector = ".smarty-choice-list .smarty-choice";

var smartyAmbiguousChoiceIndexAttribute = "data-index";

var SmartyStreetsVerify = class {
	constructor(options) {
		if (!options || !options.addressFormSelector || !options.address1InputSelector || !options.cityInputSelector || !options.stateInputSelectSelector ||
			!options.stateInputTextSelector || !options.stateInputSharedSelector || !options.postalCodeInputSelector) {
			LoggerInstance.error("Initialize: options not correct.", options);
		}

		//Short circuit if the address form did not exist or did not have the inputs necessary for verification.
		if (!$(options.addressFormSelector).length) {
			return;
		}

		this.address1InputSelector = options.address1InputSelector;
		this.address2InputSelector = options.address2InputSelector; //optional
		this.address3InputSelector = options.address3InputSelector; //optional
		this.address4InputSelector = options.address4InputSelector; //optional
		this.cityInputSelector = options.cityInputSelector;
		this.stateInputSelectSelector = options.stateInputSelectSelector;
		this.stateInputTextSelector = options.stateInputTextSelector;
		this.stateInputSharedSelector = options.stateInputSharedSelector;
		this.postalCodeInputSelector = options.postalCodeInputSelector;
		this.countryInputSelector = options.countryInputSelector; //optional
		this.addressFormSelector = options.addressFormSelector;
		this.addressVerifiedAttribute = Constants.ADDRESS_VERIFICATION_STATUSES_ATTRIBUTES.VERIFIED;
		this.addressUserVerifiedAttribute = Constants.ADDRESS_VERIFICATION_STATUSES_ATTRIBUTES.USER_CERTIFIED;
		this.addressVerificationErrorAttribute = Constants.ADDRESS_VERIFICATION_STATUSES_ATTRIBUTES.ERROR;
		this.restrictedCountries = options.restrictedCountries;

		this.missingSecondaryPopupHtml = options.missingSecondaryPopupHtml;
		this.notEnoughInformationPopupHtml = options.notEnoughInformationPopupHtml;
		this.unknownAddressPopupHtml = options.unknownAddressPopupHtml;
		this.ambiguousAddressPopupHtml = options.ambiguousAddressPopupHtml;

		this.addListener(this.address1InputSelector);
		this.addListener(this.address2InputSelector);
		this.addListener(this.address3InputSelector);
		this.addListener(this.address4InputSelector);
		this.addListener(this.cityInputSelector);
		this.addListener(this.stateInputSharedSelector);
		this.addListener(this.postalCodeInputSelector);
		this.addListener(this.countryInputSelector);

		this.initialized = true;
	}

	_isAddressFormVerifiable(options) {
		var addressInputs = this._getAddressInputs(options);

		var hasCountry = addressInputs.country.length || window.AccountStore.getDefaultCountry()
		var hasStreet = addressInputs.address1.length;
		var hasCity = addressInputs.city.length;
		var hasPostalCode = addressInputs.postalCode.length;
		var hasState = addressInputs.state.length;

		return hasCountry && hasStreet && (hasPostalCode || (hasCity && hasState));
	}

	_getAddressInputs(options) {
		options = options || this;

		// Only select :visible elements because there may be duplicate fields in individual/organization account type regions
		return {
			address1: $(options.address1InputSelector).filter(":visible"),
			address2: $(options.address2InputSelector).filter(":visible"),
			address3: $(options.address3InputSelector).filter(":visible"),
			address4: $(options.address4InputSelector).filter(":visible"),
			city: $(options.cityInputSelector).filter(":visible"),
			postalCode: $(options.postalCodeInputSelector).filter(":visible"),
			state: $(options.stateInputSelectSelector).filter(":visible") || $(options.stateInputTextSelector).filter(":visible"),
			country: $(options.countryInputSelector)
		};
	}

	addListener(selector) {
		if (selector) {
			$(selector).off("keyup.smartyStreetsVerify");
			$(selector).on("keyup.smartyStreetsVerify", (event) => {
				if (event && event.which === Constants.KEYS.TAB) {
					// we don't want to consider tabbing through the form a "value changed" event.
					return;
				}
				this._anyAddressDataChanged();
			});
		}
	}

	isAddressVerified() {
		return this.initialized && ($(this.addressFormSelector).attr(this.addressVerifiedAttribute) === "true" || $(this.addressFormSelector).attr(this.addressUserVerifiedAttribute) === "true");
	}

	clearVerification() {
		this._setAddressUnverified();
	}

	_hasPendingVerification() {
		return this.currentVerificationDeferred && this.currentVerificationDeferred.state() === "pending";
	}

	_storeCurrentVerificationDeferred(deferred) {
		deferred = deferred || new $.Deferred();

		this.currentVerificationDeferred = deferred;
		deferred.always(() => {
			if (this.currentVerificationDeferred === deferred) {
				this.currentVerificationDeferred = null;
			}
		});

		return deferred;
	}

	_applyDeferredHandlersToCurrentVerification(deferred) {
		deferred = deferred || new $.Deferred();

		if (!this.currentVerificationDeferred || this.currentVerificationDeferred === deferred) {
			return deferred;
		}

		this.currentVerificationDeferred.done((...args) => {
			deferred.resolve.apply(deferred, args);
		});

		this.currentVerificationDeferred.fail((...args) => {
			deferred.reject.apply(deferred, args);
		});

		return deferred;
	}

	verifyAddress(secondaryInformation, deferred) {
		if (!this.initialized || !this._isAddressFormVerifiable()) {
			deferred = deferred || new $.Deferred();
			deferred.resolve();
			//If we are not initialized, we will not verify the address, but it is not an error
			return deferred;
		}

		// Return the in-progress verification if there is one.
		if (this._hasPendingVerification() && this.currentVerificationDeferred !== deferred) {
			LoggerInstance.debug("Verify Address: Reusing in-progress address verification.");
			return this._applyDeferredHandlersToCurrentVerification(deferred);
		}

		if (this.isAddressVerified()) {
			return ApiHelper.logDebugAndReturnResolvedDeferred(LoggerInstance, "Address already verified", deferred);
		}

		// If no address data is provided by the user, and none of the address inputs are required, do not attempt verification
		if (this._isAddressEmptyAndVoluntary()) {
			deferred = deferred || new $.Deferred();
			deferred.resolve();
			return deferred;
		}

		if (!this._isEnoughInformation()) {
			deferred = this._storeCurrentVerificationDeferred(deferred);
			this._showNotEnoughInformationMessage(deferred);
			return deferred;
		}

		var country = this._getSelectedCountry();
		if (!this.restrictedCountries.some(restrictedCountry => restrictedCountry === country)) {
			return this._verifyAddress(secondaryInformation, deferred);
		}

		//This will resolve, we can continue with the submitting process
		return ApiHelper.logDebugAndReturnResolvedDeferred(LoggerInstance, `Addresses from ${country} (a restricted country) will not call SmartyStreets`, deferred);
	}

	_setAddressVerificationErrored(errorMessage) {
		$(this.addressFormSelector).attr(this.addressVerificationErrorAttribute, errorMessage || "true");
	}

	_setAddressVerified() {
		$(this.addressFormSelector).attr(this.addressVerifiedAttribute, "true");
	}

	_setAddressUnverified() {
		$(this.addressFormSelector).removeAttr(this.addressVerifiedAttribute).removeAttr(this.addressUserVerifiedAttribute).removeAttr(this.addressVerificationErrorAttribute);
	}

	_anyAddressDataChanged() {
		this._setAddressUnverified();
		//Even if we are verifying, we need to verify again because the address data has changed.
		this.currentVerificationDeferred = null;
	}

	_setAddressValueIfChanged($element, newValue) {
		if ($element.length && $element.val() !== newValue) {
			$element.val(newValue).change();
		}
	}

	_getCurrentAddressData() {
		var addressInputs = this._getAddressInputs();

		var country = this._getSelectedCountry();
		var street = addressInputs.address1.val();
		var street2 = addressInputs.address2.val();
		var street3 = addressInputs.address3.val();
		var street4 = addressInputs.address4.val();
		var city = addressInputs.city.val();
		var zipcode = addressInputs.postalCode.val();
		var state = addressInputs.state.val();

		var addressInformation = {
			city,
			country,
			state,
			street,
			street2,
			street3,
			street4,
			zipcode
		};
		this._deleteFalsyValues(addressInformation);

		return addressInformation;
	}

	_currentAddressToString(options) {
		options = options || {};
		var address = this._getCurrentAddressData();

		var addressString = "";
		addressString = this._appendToString(addressString, address.street);
		addressString = this._appendToString(addressString, address.street2, ", ");
		addressString = this._appendToString(addressString, address.street3, ", ");
		addressString = this._appendToString(addressString, address.street4, ", ");

		var cityStateString = "";
		cityStateString = this._appendToString(cityStateString, address.city, ", ");

		if (!options.shouldRemoveState) {
			cityStateString = this._appendToString(cityStateString, $(`${this.stateInputSelectSelector} option[value='${address.state}'], ${this.stateInputTextSelector}`).filter(":visible").text() || address.state, ", ");
		}
		if (!options.shouldRemovePostalCode) {
			cityStateString = this._appendToString(cityStateString, address.zipcode, (address.state && !options.shouldRemoveState ? " " : ", "));
		}

		cityStateString = this._appendToString(cityStateString, $(`${this.countryInputSelector} option[value='${address.country}']`).filter(":visible").text() || address.country, ", ");

		return this._appendToString(addressString, cityStateString, "<br>");
	}

	_appendToString(currentString, newValue, delimiter) {
		delimiter = delimiter || " ";

		if (newValue) {
			if (currentString) {
				currentString += delimiter;
			}
			currentString += newValue;
		}
		return currentString;
	}

	_getSelectedCountry() {
		return $(this.countryInputSelector).filter(":visible").val() || window.AccountStore.getDefaultCountry();
	}

	_verifyAddress(secondaryInformation, deferred) {
		var addressInformation = this._getCurrentAddressData();
		if (secondaryInformation && secondaryInformation.length) {
			addressInformation.secondaryNumber = secondaryInformation;
		}

		deferred = this._storeCurrentVerificationDeferred(deferred);

		AccountApi.standardizeAddress(addressInformation).done((response) => {
			this._verifyAddressSuccess(response, deferred, addressInformation);
		}).fail((jqXHR, textStatus, errorThrown) => {
			this._handleStandardizationError(deferred, jqXHR, errorThrown);
		});

		return deferred;
	}

	_handleStandardizationError(deferred, jqXHR, errorThrown) {
		this._setAddressVerificationErrored(`${jqXHR.status}: ${errorThrown}`);
		deferred.resolve();
	}

	_verifyAddressSuccess(response, deferred, addressInformation) {
		var currentAddressInformation = this._getCurrentAddressData();

		if (addressInformation.street !== currentAddressInformation.street || addressInformation.street2 !== currentAddressInformation.street2 ||
			addressInformation.city !== currentAddressInformation.city || addressInformation.zipcode !== currentAddressInformation.zipcode ||
			addressInformation.state !== currentAddressInformation.state || addressInformation.country !== currentAddressInformation.country) {
			LoggerInstance.debug("The address has changed so we will not change any data or mark the address as verified.");
			return;
		}

		if (this._isMissingSecondaryResponse(response)) {
			//Missing Secondary Information
			this._showMissingSecondaryInformation(response, deferred);
		} else if (this._isInvalidResponse(response)) {
			//Invalid
			this._showUnknownAddress(response, deferred);
		} else if (this._isAmbiguousResponse(response)) {
			//Ambiguous
			this._showAmbiguousAddress(response, deferred);
		} else if (this._isSkippedStandardizationResponse(response)) {
			//Skipped
			LoggerInstance.debug(`The country for the address (${addressInformation.country}) is not configured to attempt standardization.`);
			deferred.resolve();
		} else if (this._isValid(response)) {
			//Verified
			var matchInfo = response.candidates[0];

			this._setAddressFields(matchInfo);
			this._setAddressVerified();
			deferred.resolve();
		} else {
			deferred.reject("Unknown Error");
		}
	}

	_deleteFalsyValues(obj) {
		//Delete values that are not set
		Object.keys(obj).forEach(key => {
			if (!obj[key]) {
				delete obj[key];
			}
		});
	}

	_isAddressEmptyAndVoluntary() {
		var addressInputs = this._getAddressInputs();
		var isAddressEmptyAndVoluntary = true;

		Object.keys(addressInputs).forEach((key) => {
			var $input = addressInputs[key];

			if ($input.val() || $input.prop("required")) {
				isAddressEmptyAndVoluntary = false;
			}
		});

		return isAddressEmptyAndVoluntary;
	}

	_isEnoughInformation() {
		var address = this._getCurrentAddressData();

		return address.country && address.street && (address.zipcode || (address.city && address.state));
	}

	_isAmbiguousResponse(addressResponse) {
		return addressResponse && (
			(addressResponse.candidates && addressResponse.candidates.length > 1 ) || addressResponse.shouldPromptUserConfirmation
		);
	}

	_isInvalidResponse(addressResponse) {
		return (!addressResponse || !addressResponse.candidates || addressResponse.candidates.length === 0 ||
			(addressResponse.candidates.length === 1 && !addressResponse.isSuccessful));
	}

	_isMissingSecondaryResponse(addressResponse) {
		return addressResponse && addressResponse.isMissingSecondaryNumber;
	}

	_isSkippedStandardizationResponse(addressResponse) {
		return addressResponse &&
			addressResponse.isSuccessful &&
			addressResponse.candidates &&
			addressResponse.candidates.length === 1 &&
			!addressResponse.candidates[0].validatedDate;
	}

	_isValid(addressResponse) {
		return addressResponse &&
			addressResponse.isSuccessful &&
			addressResponse.candidates &&
			addressResponse.candidates.length === 1 &&
			addressResponse.candidates[0].validatedDate !== null;
	}

	_setAddressFields(addressResponse) {
		if (!addressResponse) {
			LoggerInstance.error("Could not set fields since addressResponse is falsy.", addressResponse);
			return;
		}

		var dataChanged = false;
		dataChanged = this._setSingleAddressField(addressResponse.line1, this.address1InputSelector) || dataChanged;
		dataChanged = this._setSingleAddressField(addressResponse.line2, this.address2InputSelector) || dataChanged;
		dataChanged = this._setSingleAddressField(addressResponse.line3, this.address3InputSelector) || dataChanged;
		dataChanged = this._setSingleAddressField(addressResponse.line4, this.address4InputSelector) || dataChanged;
		dataChanged = this._setSingleAddressField(addressResponse.city, this.cityInputSelector) || dataChanged;
		dataChanged = this._setSingleAddressField(addressResponse.state, this.stateInputSharedSelector) || dataChanged;
		dataChanged = this._setSingleAddressField(addressResponse.postalCode, this.postalCodeInputSelector) || dataChanged;

		//Triggering a change so that shipping and tax can recalculate if necessary, both tax and shipping listen to postalCode
		if (dataChanged) {
			$(this.postalCodeInputSelector).trigger("change");
		}
	}

	_setSingleAddressField(value, selector) {
		var valueChanged = false;
		var $selectedElement = $(selector).filter(":visible");

		if ($selectedElement.length) {
			if ($selectedElement.val() !== value) {
				valueChanged = true;
			}
			//Change is not called here because we are doing this from validation, not a manual user change
			$selectedElement.val(value);
		}

		return valueChanged;
	}

	_getValueFromSmartyStreetAddressComponents(addressComponents, fields, delimiter) {
		var value = "";
		if (!Array.isArray(fields)) {
			value = addressComponents[fields];
		} else {
			value = this._setSingleAddressDataFromFieldArray(addressComponents, fields, delimiter);
		}
		return value;
	}

	_setSingleAddressDataFromFieldArray(addressComponents, fields, delimiter) {
		var value = "";
		delimiter = delimiter || " ";
		fields.forEach(field => {
			var singleValue = addressComponents[field];
			if (!singleValue) {
				return;
			}

			if (value !== "") {
				value += delimiter;
			}
			value += singleValue;
		});

		return value;
	}

	_formatResponseAddress(addressResponse) {
		if (!addressResponse) {
			LoggerInstance.error("Cannot format address since addressResponse is falsy.", addressResponse);
			return;
		}

		var addressString = "";
		addressString = this._appendToString(addressString, addressResponse.line1, ", ");
		addressString = this._appendToString(addressString, addressResponse.line2, ", ");
		addressString = this._appendToString(addressString, addressResponse.line3, ", ");
		addressString = this._appendToString(addressString, addressResponse.line4, ", ");
		addressString = this._appendToString(addressString, addressResponse.city, "<br>");
		addressString = this._appendToString(addressString, addressResponse.state, ", ");
		addressString = this._appendToString(addressString, addressResponse.postalCode, (addressResponse.state ? " " : ", "));

		return addressString;
	}

	_setAddressInElement($element, address) {
		address = address || "";
		var addressArray = address.split("<br>");

		if (addressArray.length == 1 && !addressArray[0]) {
			return;
		}

		$element.empty();
		var first = true;
		addressArray.forEach(addressPiece => {
			if (first) {
				first = false;
			} else {
				$element.append("<br>");
			}
			$element.append(document.createTextNode(addressPiece));
		});
	}

	_showAmbiguousAddress(response, deferred) {
		if (!response || !response.candidates) {
			LoggerInstance.error("Trying to Show Ambiguous Address Popup, but there are no addresses.");
			return;
		}

		var $ambiguousHtml = $(this.ambiguousAddressPopupHtml);

		var first = true;
		var $smartyChoice = $ambiguousHtml.find(".smarty-choice");
		//Adding the data for each good address, so the constituent can pick one
		for (var index = 0; index < response.candidates.length; index++) {
			var address = response.candidates[index];
			var $currentChoice;

			if (first) {
				$currentChoice = $smartyChoice;
				first = false;
			} else {
				$currentChoice = $smartyChoice.clone();
				$smartyChoice.after($currentChoice);
			}
			this._setAddressInElement($currentChoice.attr(smartyAmbiguousChoiceIndexAttribute, index), this._formatResponseAddress(address));
		}

		var isUsOrCanada = this.isCountryInUsOrCanada(this._getSelectedCountry());

		//Set currentAddress
		this._setAddressInElement(
			$ambiguousHtml.find(smartyPopupTypedAddressSelector),
			this._currentAddressToString({
				shouldRemoveState: response.shouldRemoveState && !isUsOrCanada,
				shouldRemovePostalCode: response.shouldRemovePostalCode && !isUsOrCanada
			})
		);

		//Adding click event for when picking a good address
		this._showAndAddClickEvents($ambiguousHtml, deferred, response.shouldRemoveState, response.shouldRemovePostalCode, $popup => {
			$popup.find(smartyAmbiguousChoiceSelector).click({ addresses: response, deferred, self: this }, function (event) {
				if (!event || !event.data || !event.data.self || !event.data.addresses) {
					ApiHelper.logErrorAndReturnRejectedDeferred(LoggerInstance, "Parameters not passed correctly to Ambiguous Choice Popup.", deferred);
					return;
				}
				var self = event.data.self;
				var candidates = event.data.addresses.candidates;

				var index = $(this).attr(smartyAmbiguousChoiceIndexAttribute);
				var matchInfo = candidates[index];

				self._setAddressFields(matchInfo);
				self._setAddressVerified();

				self._removePopup($popup);

				deferred.resolve();
			});
		});
	}

	_showUnknownAddress(response, deferred) {
		this._showInvalidAddress(this.unknownAddressPopupHtml, deferred, response);
	}

	_showNotEnoughInformationMessage(deferred) {
		this._showInvalidAddress(this.notEnoughInformationPopupHtml, deferred);
	}

	_showInvalidAddress(html, deferred, response) {
		var $unknownAddressHtmlElement = $(html);
		var isUsOrCanada = this.isCountryInUsOrCanada(this._getSelectedCountry());
		if (response) {
			var {shouldRemoveState, shouldRemovePostalCode} = response;
		}

		this._setAddressInElement(
			$unknownAddressHtmlElement.find(smartyPopupTypedAddressSelector),
			this._currentAddressToString({
				shouldRemoveState: shouldRemoveState && !isUsOrCanada,
				shouldRemovePostalCode: shouldRemovePostalCode && !isUsOrCanada
			})
		);

		this._showAndAddClickEvents($unknownAddressHtmlElement, deferred, shouldRemoveState, shouldRemovePostalCode);
	}

	_showMissingSecondaryInformation(response, deferred) {
		if (!response || !response.candidates || response.candidates.length <= 0) {
			LoggerInstance.error("Trying to Show Missing Secondary Information Popup, but there is no address returned from the validation.");
			return;
		}

		var $missingSecondaryHtmlElement = $(this.missingSecondaryPopupHtml);
		this._setAddressInElement($missingSecondaryHtmlElement.find(smartyPopupTypedAddressSelector), this._formatResponseAddress(response.candidates[0]));

		//Adding click event for when picking a good address
		this._showAndAddClickEvents($missingSecondaryHtmlElement, deferred, response.shouldRemoveState, response.shouldRemovePostalCode, $popup => {
			function processSecondarySubmit(verifyInstance) {
				var secondaryInformation = $(smartySecondaryInputSelector).val();
				if (!secondaryInformation) {
					ApiHelper.logErrorAndReturnRejectedDeferred(LoggerInstance, "Secondary Input Box is blank.", deferred);
					return;
				}

				if (!verifyInstance || !verifyInstance.self) {
					ApiHelper.logErrorAndReturnRejectedDeferred(LoggerInstance, "Parameters not passed correctly to Secondary Information Popup Submit.", deferred);
					return;
				}

				if (secondaryInformation) {
					verifyInstance.self.verifyAddress(secondaryInformation, deferred);
				}
				verifyInstance.self._removePopup($popup);
			};

			//Submit button clicked
			$popup.find(smartySecondarySubmitSelector).click({ deferred, self: this }, function (event) {
				if (!event) {
					ApiHelper.logErrorAndReturnRejectedDeferred(LoggerInstance, "Parameters not passed correctly to Secondary Information Popup Click Event.", deferred);
					return;
				}
				processSecondarySubmit(event.data);
			});

			//Enter being pressed in Input
			$popup.find(smartySecondaryInputSelector).keydown({ deferred, self: this }, (event) => {
				if (!event) {
					ApiHelper.logErrorAndReturnRejectedDeferred(LoggerInstance, "Parameters not passed correctly to Secondary Information Popup KeyDown Event.", deferred);
					return;
				}

				switch (event.which) {
					case Constants.KEYS.ENTER:
						processSecondarySubmit(event.data);
						break;
				}
			});
		});

		$(smartySecondaryInputSelector).focus();
	}

	_showAndAddClickEvents(html, deferred, shouldRemoveState, shouldRemovePostalCode, additionalEventInitializer) {
		var $popup = $(html).hide().appendTo("body");
		var isUsOrCanada = this.isCountryInUsOrCanada(this._getSelectedCountry());
		var shouldHideUseAsIsButton = isUsOrCanada && (shouldRemoveState || shouldRemovePostalCode);

		$popup.find(smartyPopupSelector).addBack(smartyPopupSelector).css({
			left: `calc(50% - ${($popup.width() / 2)}px)`,
			top: "25%",
			position: "fixed"
		});

		if (shouldHideUseAsIsButton) {
			$popup.find(smartyConstituentCertifiedSelector).parent().remove();
		}

		$popup.show();

		//Adding Click Events
		$popup.find(smartyGoBackSelector).click(() => {
			//Constituent wants to go back and correct or ignore.
			this._removePopup($popup);
			deferred.reject("Constituent will correct address");
		});

		if (!shouldHideUseAsIsButton){
			$popup.find(smartyConstituentCertifiedSelector).click(() => {
				//Constituent certifies the address is correct.
				$(this.addressFormSelector).attr(this.addressUserVerifiedAttribute, "true");

				//We don't accept States if the address is User Certified, we are Auto-Adding State Codes, and the provided state was not discovered.
				var userEnteredState = $(this.stateInputTextSelector).filter(":visible").val();
				if (userEnteredState && shouldRemoveState) {
					this._setSingleAddressField(null, this.stateInputSharedSelector);
				}

				//We don't accept Postal Codes if the address is User Certified, we are Auto-Adding Postal Codes, and the provided postal code was not discovered.
				var userEnteredPostalCode = $(this.postalCodeInputSelector).filter(":visible").val();
				if (userEnteredPostalCode && shouldRemovePostalCode) {
					this._setSingleAddressField(null, this.postalCodeInputSelector).filter(":visible");
				}

				this._removePopup($popup);

				deferred.resolve("Constituent certified address");
			});
		}

		//Escape linked to go back and correct or ignore.
		$(document).on("keydown", { $popup }, this._popupEscapeHandler);

		//Adding additional click events
		if (typeof additionalEventInitializer === "function") {
			additionalEventInitializer($popup);
		}
	}

	_popupEscapeHandler(event) {
		if (!event || !event.data) {
			LoggerInstance.error("Event or Event Data not present in Popup Escape Handler");
			return;
		}

		switch (event.which) {
			case Constants.KEYS.ESCAPE:
				$(event.data.$popup).find(smartyGoBackSelector).click();
				break;
		}
	}

	_removePopup($popup) {
		$(document).off("keydown", this._popupEscapeHandler);
		$popup.remove();
	}

	isCountryInUsOrCanada(country) {
		country = (country || "").toUpperCase();
		if (country === "US" || country === "USA" || country === "CA" || country === "CAN") {
			return true;
		}

		return false;
	}
};

module.exports = SmartyStreetsVerify;
